import { useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { CashflowFrequency } from '../utils';
import { ExpenditureCategory } from '../hooks/useExpenditures';
import { IncomeType } from '../hooks/useIncomes';
import { storedClientIdsOf } from './jointAllocations';
import { CashflowOwner, IncomeOwner } from './ownerTypes';

type JointSubmission<TOwner extends CashflowOwner> = {
  /** Absent while adding. */
  id?: string;
  owners: TOwner[];
  /** The clients the item belonged to when the form opened, none while adding. */
  storedClientIds: string[];
};

export type JointIncomeSubmission = JointSubmission<IncomeOwner> & {
  incomeType: IncomeType;
  frequency: CashflowFrequency;
  description: string;
  amountPreTax: number | null;
};

export type JointExpenditureSubmission = JointSubmission<CashflowOwner> & {
  category: ExpenditureCategory;
  frequency: CashflowFrequency;
  description: string;
  amount: number | null;
};

export type JointLifeEventSubmission = JointSubmission<CashflowOwner> & {
  description: string;
  eventDate: string;
  details: string;
  cost: number | null;
};

export type JointDeletion = { id: string; storedClientIds: string[] };

/**
 * The routes are scoped to a client, and the backend takes a write to an existing item only
 * through a client who already owns it, and a new item only through one it names. The tab's own
 * client is used where it qualifies, so the write reads as theirs, then another member of the
 * family, whom the adviser can open, before an owner from outside it.
 */
function throughClient(preferredClientIds: string[], candidates: string[]): string {
  return preferredClientIds.find(clientId => candidates.includes(clientId)) ?? candidates[0];
}

/**
 * One request saves the whole item with every owner on it, so a save either lands or does not.
 * Leaving a member out of the owners takes them off the item. Every client the item belonged to
 * before or after is read again, since each of their tabs lists it.
 */
function useSaveJointItem<TSubmission extends JointSubmission<CashflowOwner>>(
  collection: string,
  preferredClientIds: string[],
  toBody: (submission: TSubmission, clientId: string) => Record<string, unknown>,
): (submission: TSubmission) => Promise<void> {
  const queryClient = useQueryClient();

  return async submission => {
    const named = storedClientIdsOf(submission.owners);
    const clientId = throughClient(
      preferredClientIds,
      submission.id ? submission.storedClientIds : named,
    );
    try {
      // A refused owner list (422) is the form's to show, not an incident to report
      await api.put(
        `clients/${clientId}/${collection}/batch`,
        [{ id: submission.id, ...toBody(submission, clientId) }],
        { expectedStatuses: [422] },
      );
    } finally {
      await invalidate(queryClient, collection, [...submission.storedClientIds, ...named]);
    }
  };
}

/** Deleting the item deletes it for every owner, in one request through any of them. */
function useDeleteJointItem(
  collection: string,
  preferredClientIds: string[],
): (deletion: JointDeletion) => Promise<void> {
  const queryClient = useQueryClient();

  return async ({ id, storedClientIds }) => {
    const clientId = throughClient(preferredClientIds, storedClientIds);
    try {
      await api.post(`clients/${clientId}/${collection}/delete-request`, [id]);
    } finally {
      await invalidate(queryClient, collection, storedClientIds);
    }
  };
}

async function invalidate(
  queryClient: ReturnType<typeof useQueryClient>,
  collection: string,
  clientIds: string[],
): Promise<void> {
  await Promise.all(
    [...new Set(clientIds)].map(clientId =>
      queryClient.invalidateQueries({ queryKey: ['clients', clientId, collection] }),
    ),
  );
}

export function useSaveJointIncome(preferredClientIds: string[]) {
  return useSaveJointItem<JointIncomeSubmission>(
    'incomes',
    preferredClientIds,
    (submission, clientId) => ({
      description: submission.description,
      frequency: submission.frequency,
      incomeType: submission.incomeType,
      amountPreTax: submission.amountPreTax,
      // The income's own after-tax figure is the client's the request goes through; the backend
      // takes it from their owner entry, so the two are sent the same.
      amountPostTax:
        submission.owners.find(owner => owner.clientId === clientId)?.amountPostTax ?? null,
      owners: submission.owners,
    }),
  );
}

export function useSaveJointExpenditure(preferredClientIds: string[]) {
  return useSaveJointItem<JointExpenditureSubmission>(
    'expenditures',
    preferredClientIds,
    submission => ({
      description: submission.description,
      category: submission.category,
      frequency: submission.frequency,
      amount: submission.amount,
      owners: submission.owners,
    }),
  );
}

export function useSaveJointLifeEvent(preferredClientIds: string[]) {
  return useSaveJointItem<JointLifeEventSubmission>(
    'life-events',
    preferredClientIds,
    submission => ({
      description: submission.description,
      eventDate: submission.eventDate,
      details: submission.details,
      cost: submission.cost,
      owners: submission.owners,
    }),
  );
}

export function useDeleteJointIncome(preferredClientIds: string[]) {
  return useDeleteJointItem('incomes', preferredClientIds);
}

export function useDeleteJointExpenditure(preferredClientIds: string[]) {
  return useDeleteJointItem('expenditures', preferredClientIds);
}

export function useDeleteJointLifeEvent(preferredClientIds: string[]) {
  return useDeleteJointItem('life-events', preferredClientIds);
}
